<template>
  <div
    ref="gjWizard"
    class="gj-wizard"
  >
    <loading-page v-if="loading" />
    <form-wizard
      v-if="!loading"
      color="#7367F0"
      :title="null"
      :subtitle="null"
      :class="{ hidden: showCacheForm || showRequestForm }"
      shape="square"
      @on-change="wizardToTop"
      @on-complete="formSubmitted"
    >
      <!-- origin setting tab -->
      <tab-content
        title="Origin Settings"
        :before-change="validationOriginSettings"
      >
        <div class="container">
          <div class="col-12 col-lg-9 p-0 m-auto">
            <div class="d-flex justify-content-end mb-2">
              <a
                v-if="distributionsSize < 1"
                href="https://docs.captain.gjirafa.tech/cdn/user-guide/getting-started"
                target="_blank"
                class="text-main"
              >
                Need help creating your first distribution
                <img
                  :src="require('@/assets/svg/question.svg')"
                  alt="questionmark"
                >
              </a>
            </div>
            <validation-observer
              ref="originRules"
              tag="form"
            >
              <paper-simple v-if="!noOriginBuckets || externalAccounts.length > 0">
                <custom-input-radio
                  v-model="origin.originTypeId"
                  label="Origin Type"
                  :options="getOriginTypeOptions()"
                  @change="onChangeOriginId"
                />
                <custom-alert
                  icon-name="InfoAlt"
                  items-center
                  variant="light"
                >An origin can be your website URL, IP address of your server, or Captain Storage.</custom-alert>
              </paper-simple>

              <paper-simple>
                <div
                  v-if="custom"
                  class="d-flex align-items-center"
                >
                  <custom-select
                    v-model="distribution.originProtocolPolicy"
                    style="max-width:100px !important; margin-top: 7px;"
                    :options="getObjectOptions(originProtocolPolicyOptions)"
                  />
                  <validation-provider
                    #default="{ errors }"
                    class="top-fade-in w-100"
                    name="Origin Host"
                    rules="required|ip"
                  >
                    <input-field
                      label="Origin Host"
                      label-position="top"
                      class="custom-label-position"
                      :info="messages.originDomainName"
                      :error="errors.length ? errors[0] : null"
                      @openInfo="showInfo(messages.originDomainName.title, messages.originDomainName.message)"
                    >
                      <b-form-input
                        v-model="distribution.domainName"
                        placeholder="mydomain.com or 1.1.1.1"
                        @change="onChangeOriginId"
                      />
                    </input-field>
                  </validation-provider>
                </div>

                <validation-provider
                  v-else
                  #default="{ errors }"
                  class="top-fade-in"
                  name="Origin Bucket"
                  rules="required"
                >
                  <input-field
                    label="Origin Bucket"
                    class="mr-35"
                    label-position="top"
                    :error="errors.length ? errors[0] : null"
                  >
                    <div class="col-12 p-0 d-flex flex-wrap">
                      <b-dropdown
                        id="object-options"
                        :disabled="fetchingExternalAccounts"
                        variant="bg-white"
                        no-caret
                        class="col-md-6 col-sm-12 mb-sm-2 m-md-0 p-0"
                        :state="errors.length > 0 ? false : null"
                      >
                        <template
                          #button-content
                        >
                          <div @click="toggleOpenExternalAccount">
                            <img
                              :src="selectedAccount.parentId === '0' ? require('@/assets/images/logo/Captain.svg') : selectedAccount.providerImageUrl"
                              width="30px"
                              height="30px"
                              alt="Storage External Account"
                              class="mr-1"
                            >
                            <span><b>{{ selectedAccount.parentId === '0' ? 'Captain' : selectedAccount.username }}</b></span>
                            <GjIcon
                              class="ml-1"
                              :class="isOpenExternalAccount ? 'arrow' : 'arrow-down'"
                              name="ArrowDown"
                            />
                          </div>
                        </template>
                        <ul
                          v-for="(account, property) in externalAccounts"
                          :key="property"
                          class="list-unstyled m-0 p-0"
                        >
                          <b-dropdown-item @click="changeSelectedAccount(account)">
                            <img
                              :src="account.parentId === '0' ? require('@/assets/images/logo/Captain.svg') : account.providerImageUrl"
                              width="30px"
                              height="30px"
                              alt=""
                              class="mr-1"
                            >
                            <span class="main"><strong>{{ account.parentId === '0' ? 'Captain ' : account.username }}</strong></span>
                          </b-dropdown-item>

                        </ul>
                      </b-dropdown>
                      <custom-select
                        id="origin-bucket"
                        v-model="selectedBucket"
                        class="col-md-6 col-sm-12 p-0 bucket-input"
                        label="name"
                        :loading="fetchingAccountBuckets || fetchingExternalAccounts"
                        :disabled="isBucketDisabled()"
                        :state="errors.length > 0 ? false:null"
                        :options="getBucketOptions(accountBuckets)"
                        @change="onChangeOriginId"
                      />
                    </div>
                  </input-field>
                </validation-provider>
                <input-field
                  label="Origin Path"
                  label-position="top"
                  :info="messages.originPath"
                  @openInfo="
                    showInfo(
                      messages.originPath.title,
                      messages.originPath.message,
                    )
                  "
                >
                  <b-form-input
                    v-model="distribution.originPath"
                    placeholder="/"
                  />
                </input-field>
                <validation-provider
                  #default="{ errors }"
                  name="Origin Id"
                  rules="required|max:50"
                >
                  <input-field
                    label="Origin ID"
                    label-position="top"
                    :info="messages.originId"
                    :error="errors.length ? errors[0] : null"
                    @openInfo="
                      showInfo(
                        messages.originId.title,
                        messages.originId.message,
                      )
                    "
                  >
                    <b-form-input
                      v-model="distribution.originId"
                      :state="errors.length > 0 ? false : null"
                      @change="originIdChanged = true"
                    />
                  </input-field>
                </validation-provider>
              </paper-simple>

              <template v-if="custom">
                <paper-simple class="top-fade-in">

                  <formInput
                    no-icon
                    title="HTTP Port"
                  >
                    <template v-slot:input>
                      <b-form-input
                        v-model="distribution.httpPort"
                        :disabled="distribution.originProtocolPolicy === 1"
                      />
                      <custom-alert icon-name="Info">
                        {{ messages.httpPort.message }}
                      </custom-alert>
                    </template>
                  </formInput>

                  <formInput
                    title="HTTPS Port"
                    @info="
                      showInfo(
                        messages.httpsPort.title,
                        messages.httpsPort.message,
                      )
                    "
                  >
                    <template v-slot:input>
                      <b-form-input
                        v-model="distribution.httpsPort"
                        :disabled="distribution.originProtocolPolicy === 0"
                      />
                    </template>
                  </formInput>
                  <formInput
                    title="Origin Custom Headers"
                    no-icon
                  >
                    <template v-slot:input>
                      <template v-for="header in originHeaders">
                        <div
                          :key="header.index"
                          class="d-flex align-items-center mt-1 position-relative"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :vid="`key${header.index}`"
                            name="Key"
                            rules="required"
                            class="w-50 icon-left"
                          >
                            <b-input
                              :id="`key${header.index}`"
                              v-model="header.key"
                              :state="errors.length > 0 ? false : null"
                              placeholder="Key"
                              class="mb-0"
                            />
                          </validation-provider>
                          <validation-provider
                            #default="{ errors }"
                            :vid="`header${header.index}`"
                            name="Value"
                            rules="required"
                            class="w-50"
                          >
                            <b-input
                              :id="`value${header.index}`"
                              v-model="header.value"
                              :state="errors.length > 0 ? false : null"
                              placeholder="Value"
                              class="mb-0 mr-0"
                            />
                          </validation-provider>
                          <a
                            class="right-icon"
                            @click="onRemoveField(header.index)"
                          >
                            <GjIcon name="Delete" />
                          </a>
                        </div>
                      </template>
                      <div class="px-1 px-md-2 mt-1">
                        <a
                          class="text-primary text-medium"
                          @click="onAddField"
                        >
                          + Add header
                        </a>
                      </div>
                    </template>
                  </formInput>
                </paper-simple>
              </template>
              <template v-else>
                <paper-simple>
                  <custom-input-radio
                    v-model="origin.bucketAccessId"
                    label="Bucket Access"
                    :info="messages.bucketAccess"
                    :options="getObjectOptions(origin.bucketAccessOptions)"
                    @openInfo="
                      showInfo(
                        messages.bucketAccess.title,
                        messages.bucketAccess.message,
                      )
                    "
                  />
                </paper-simple>
              </template>
            </validation-observer>
          </div>
        </div>
      </tab-content>

      <!-- default cache behavior settings tab -->
      <tab-content title="Default Cache Behavior Settings">
        <div class="container">
          <div class="col-12 col-lg-9 p-0 m-auto">
            <paper-simple>
              <input-field
                label="Path Pattern"
                label-position="top"
                :info="messages.pathPattern"
                @openInfo="
                  showInfo(
                    messages.pathPattern.title,
                    messages.pathPattern.message,
                  )
                "
              >
                <b-form-input
                  value="Default (*)"
                  disabled
                />
              </input-field>
            </paper-simple>

            <paper-simple>
              <formInput
                no-icon
                title="Allowed HTTP Methods"
              >
                <template v-slot:input>
                  <b-form-radio-group
                    v-if="behaviorModel.allowedHttpMethodsOptions"
                    v-model="distribution.allowedHttpMethods"
                    class="mb-1"
                    :options="
                      getOptions(behaviorModel.allowedHttpMethodsOptions)
                    "
                    stacked
                  />
                  <custom-alert icon-name="Info">
                    {{ messages.allowedHttpMethods.message }}
                  </custom-alert>
                </template>
              </formInput>

              <formInput
                no-icon
                title="Cached HTTP Methods"
              >
                <template v-slot:input>
                  <b-form-radio-group
                    v-if="behaviorModel.cachedHttpMethodsOptions"
                    v-model="distribution.cachedHttpMethods"
                    class="mb-1"
                    :options="
                      getOptions(behaviorModel.cachedHttpMethodsOptions)
                    "
                    stacked
                  />
                  <custom-alert icon-name="Info">
                    {{ messages.cachedHttpMethods.message }}
                  </custom-alert>
                </template>
              </formInput>
            </paper-simple>

            <paper-simple>
              <input-field
                label="Cache Policy"
                label-position="top"
                info-position="label"
                :info="messages.cachePolicy"
                @openInfo="
                  showInfo(
                    messages.cachePolicy.title,
                    messages.cachePolicy.message,
                  )
                "
              >
                <div class="w-100">
                  <custom-select
                    v-if="behaviorModel.cachePolicyOptions"
                    v-model="distribution.cachePolicyId"
                    :options="getOptions(behaviorModel.cachePolicyOptions)"
                  />
                  <div class="d-flex flex-wrap">
                    <b-button
                      variant="bg-white"
                      class="icon-left mt-1"
                      target="_blank"
                      :href="
                        `/${projectId}/cache-policy/${distribution.cachePolicyId}/info`
                      "
                    >
                      View policy details
                    </b-button>
                    <b-button
                      variant="bg-white"
                      class="mt-1"
                      @click="showCacheForm = true"
                    >
                      <GjIcon
                        class="icon-left"
                        name="Plus"
                      />
                      Create a new policy
                    </b-button>
                  </div>
                </div>
              </input-field>
            </paper-simple>

            <paper-simple>
              <input-field
                label="Origin Request Policy"
                label-position="top"
                info-position="label"
                :info="messages.originRequestPolicies"
                @openInfo="
                  showInfo(
                    messages.originRequestPolicies.title,
                    messages.originRequestPolicies.message,
                  )
                "
              >
                <div class="w-100">
                  <custom-select
                    v-if="behaviorModel.originRequestPolicyOptions"
                    v-model="distribution.originRequestPolicyId"
                    :options="
                      getOptions(behaviorModel.originRequestPolicyOptions)
                    "
                  />
                  <div class="d-flex flex-wrap">
                    <b-button
                      variant="bg-white"
                      class="icon-left mt-1"
                      target="_blank"
                      :href="
                        `/${projectId}/request-policy/${distribution.originRequestPolicyId}/info`
                      "
                    >
                      View policy details
                    </b-button>
                    <b-button
                      variant="bg-white"
                      class="mt-1"
                      @click="showRequestForm = true"
                    >
                      <GjIcon
                        class="icon-left"
                        name="Plus"
                      />
                      Create a new policy
                    </b-button>
                  </div>
                </div>
              </input-field>
            </paper-simple>
          </div>
        </div>
      </tab-content>

      <!-- distribution settings tab  -->
      <tab-content title="Distribution Settings">
        <div class="container">
          <div class="col-12 col-lg-9 p-0 m-auto">
            <paper-simple>
              <input-field
                label="Default Root Object"
                label-position="top"
                :info="messages.defaultRootObject"
                @openInfo="
                  showInfo(
                    messages.defaultRootObject.title,
                    messages.defaultRootObject.message,
                  )
                "
              >
                <b-form-input
                  v-model="distribution.defaultRootObject"
                  placeholder="Enter default root object here..."
                />
              </input-field>

              <input-field
                label="Web Application Firewall"
                label-position="top"
                class="mr-35"
              >
                <custom-select
                  v-if="distributionModel.wafSettingOptions.length > 0"
                  v-model="distribution.wafSetting"
                  :options="getOptions(distributionModel.wafSettingOptions)"
                />
              </input-field>
              <custom-input-radio
                v-model="distribution.sslCertificateType"
                :info="sslInfo"
                :options="getOptions(distributionModel.sslCertificateOptions)"
                @openInfo="showInfo(sslTitle, sslMessage)"
              />
              <input-field
                v-if="distribution.sslCertificateType === 4"
                class="my-1 mx-50"
                label="Select Certificate"
                label-position="top"
              >
                <custom-select
                  v-model="distribution.sslCertificateId"
                  :options="formattedSniCertificates"
                  placeholder="Select a certificate"
                />
              </input-field>
            </paper-simple>
          </div>
        </div>
      </tab-content>
      <template
        slot="footer"
        slot-scope="props"
      >
        <div class="wizard-custom-footer col-12 col-lg-9">
          <b-button
            variant="bg-white"
            class="mr-1"
            :class="props.activeTabIndex > 0 ? 'transparent' : ''"
            @click="onCancel()"
          >
            Cancel
          </b-button>
          <b-button
            v-if="props.activeTabIndex > 0"
            variant="bg-white"
            class="mr-1"
            @click="props.prevTab()"
          >
            Go Back
          </b-button>
          <b-button
            v-if="!props.isLastStep"
            class="wizard-footer-right"
            variant="bg-primary"
            @click="props.nextTab()"
          >
            Next
          </b-button>
          <button-loading
            v-else
            class="wizard-footer-right done-button"
            :spinning="addingDistribution"
            variant="bg-primary"
            @click="formSubmitted()"
          >Done</button-loading>
        </div>
      </template>
    </form-wizard>
    <template v-if="showCacheForm">
      <cache-form
        @close="showCacheForm = false"
        @onCreate="onCreateCachePolicies()"
      />
    </template>

    <template v-if="showRequestForm">
      <request-form
        @close="showRequestForm = false"
        @onCreate="onCreateRequestPolicies()"
      />
    </template>

    <info
      v-if="openInfo"
      :title="infoTitle"
      :message="infoMessage"
      @close="openInfo = false"
    />
  </div>
</template>
<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, ip, max } from '@validations'
import CachePoliciesFrom from '@/views/cdn/policies/cache/CachePoliciesForm.vue'
import RequestPoliciesFrom from '@/views/cdn/policies/request/RequestPoliciesForm.vue'
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import messages from '../../messages'

export default {
  name: 'DistributionNew',
  components: {
    FormWizard,
    TabContent,
    ValidationProvider,
    ValidationObserver,
    'cache-form': CachePoliciesFrom,
    'request-form': RequestPoliciesFrom,
    CustomInputRadio,
  },
  data() {
    return {
      required,
      ip,
      max,
      messages,
      openInfo: false,
      infoTitle: '',
      infoMessage: '',
      behaviorModel: {},
      originHeaders: [],
      cachePolicyOptions: [],
      requestPolicyOptions: [],
      originProtocolPolicyOptions: {
        'http://': 0,
        'https://': 1,
      },
      originIdChanged: false,
      showCacheForm: false,
      showRequestForm: false,
      noOriginBuckets: false,
      isOpenExternalAccount: false,
      selectedBucket: '',
      formInitialized: false,
    }
  },
  computed: {
    lastIndex() {
      return this.originHeaders.length
        ? this.originHeaders[this.originHeaders.length - 1].index
        : 0
    },
    firstIndex() {
      return this.originHeaders[0].index
    },
    originId() {
      if (this.distribution.domainName !== '') {
        return `custom-${this.distribution.domainName}`
      }
      return ''
    },
    distributionsSize() {
      return this.$store.getters['cdn/distributionsSize']
    },
    distributionModel() {
      return this.$store.state.cdn.distribution
    },
    distribution() {
      return this.$store.state.cdn.newDistribution
    },
    breakpoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    origin() {
      return this.$store.state.cdn.origin
    },
    addingDistribution() {
      return this.$store.state.cdn.addingDistribution
    },
    custom() {
      return this.origin.originTypeId === 0
    },
    gettingOrigin() {
      return this.$store.state.cdn.gettingOrigin
    },
    gettingDistribution() {
      return this.$store.state.gettingDistribution
    },
    gettingBehavior() {
      return this.$store.state.gettingBehavior
    },
    loading() {
      return (
        this.gettingOrigin || this.gettingDistribution || this.gettingBehavior
      )
    },
    originBucketId() {
      const foundOriginBucket = this.origin.bucketListModel.find(b => b.name === this.origin.bucket)
      return foundOriginBucket?.name ? foundOriginBucket.name : this.accountBuckets[0]?.name
    },
    projectId() {
      return this.$store.state.app.projectId
    },
    sslTitle() {
      return this.distribution.sslCertificateType === 2 ? this.messages.instantSSL.title : this.messages.noSSL.title
    },
    sslMessage() {
      // Define the message for the <info> component based on the sslCertificateType value
      return this.distribution.sslCertificateType === 2 ? this.messages.instantSSL.message : this.messages.noSSL.message
    },
    sslInfo() {
      return this.distribution.sslCertificateType === 2 ? this.messages.instantSSL : this.messages.noSSL
    },
    sniCertificates() {
      return this.$store.state.cdn.sniCertificates
    },
    formattedSniCertificates() {
      return this.sniCertificates.map(cert => ({
        value: cert.id,
        text: this.formatCertificateLabel(cert),
        cert,
      }))
    },
    fetchingExternalAccounts() {
      return this.$store.state.externalAccount.fetchingExternalAccounts
    },
    externalAccounts() {
      return this.$store.state.externalAccount.externalAccounts
    },
    fetchingAccountBuckets() {
      return this.$store.state.accountBuckets.fetchingBuckets
    },
    accountBuckets: {
      get() {
        return this.$store.state.accountBuckets.buckets
      },
      set(account) {
        const arr = [{ ...account }]
        this.$store.commit('accountBuckets/UPDATE_BUCKETS', arr)
      },
    },
    selectedAccount: {
      get() {
        return this.$store.state.externalAccount.externalAccount || null
      },
      set(account) {
        this.$store.commit('externalAccount/CHANGE_ACCOUNT', account)
      },
    },
    accountId: {
      get() {
        return this.$store.state.externalAccount.accountId
      },
      set(val) {
        this.$store.commit('externalAccount/SET_ACCOUNT_ID', val)
      },
    },
  },
  created() {
    if (this.distribution === null) {
      this.$store.commit('cdn/SET_NEW_DISTRIBUTION')
    }
    this.getDistribution()
    this.getBehavior()
    this.getOrigin()
    this.fetchSniCertificates()
  },
  methods: {
    getOriginTypeOptions() {
      const options = this.getObjectOptions(this.origin.originTypeOptions)
        .sort((a, b) => {
          if (a.value < b.value) {
            return 1
          }
          if (a.value > b.value) {
            return -1
          }
          return 0
        })

      return options
    },
    getDistribution() {
      this.$store
        .dispatch('cdn/getDistribution', 0)
        .then(() => {
          this.distribution.wafSetting = this.distributionModel.wafSetting
          this.distribution.sslCertificateType = this.distributionModel.sslCertificateType
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while getting distribution model.')
        })
    },
    getBehavior() {
      this.$store
        .dispatch('cdn/getBehavior', {
          distributionId: 0,
          id: 0,
        })
        .then(res => {
          this.behaviorModel = res.data.result
          this.distribution.cachePolicyId = this.behaviorModel.cachePolicyOptions[0].id
          this.distribution.originRequestPolicyId = this.behaviorModel.originRequestPolicyOptions[0].id
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0])
        })
    },
    getOrigin() {
      this.$store
        .dispatch('cdn/getOrigin', {
          distributionId: 0,
          originId: 'model',
        })
        .then(() => {
          if (this.origin.bucketListModel.length < 1) {
            this.noOriginBuckets = true
            this.origin.originTypeId = 0
          }
          if (!this.noOriginBuckets && this.origin.bucket === null) {
            this.origin.bucket = this.origin.bucketListModel[0].name
            this.onChangeOriginId()
          }
          this.distribution.minimumOriginSslProtocol = this.origin.minimumOriginSslProtocol
          this.distribution.originProtocolPolicy = this.origin.originProtocolPolicy
          this.distribution.httpPort = this.origin.httpPort
          this.distribution.httpsPort = this.origin.httpsPort
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0])
        })
        .finally(() => {
          this.fetchExternalAccounts()
        })
    },
    fetchExternalAccounts() {
      this.$store
        .dispatch('externalAccount/fetchAccounts')
        .then(() => {
          this.changeSelectedAccount(this.selectedAccount)
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors
              || 'Something went wrong while fetching all external accounts.',
          )
        })
    },
    getBuckets() {
      this.$store
        .dispatch('accountBuckets/fetchBuckets', this.accountId)
        .then(() => {
          // eslint-disable-next-line prefer-destructuring
          this.selectedBucket = this.accountBuckets[0]?.name
          this.formInitialized = true
        })
        .catch(error => {
          const { status } = error.response || null
          if (status === 424) {
            this.$store.commit('accountBuckets/SET_SHOW_INTRO', true)
            this.$store.commit('buckets/SET_SHOW_INTRO', true)
            return
          }
          if (status === 426 || status === 428) {
            this.$store.commit('accountBuckets/SET_SERVICE_UNAVAILABLE', status === 428)
            this.$store.commit('buckets/SET_SERVICE_UNAVAILABLE', status === 428)
            return
          }
          if (status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching buckets.',
          )
          // eslint-disable-next-line eqeqeq
          if (this.selectedAccount.parentId == 0) {
            this.$store.dispatch('accountBuckets/clearBucket')
          }
        })
    },
    formSubmitted() {
      if (this.distribution.sslCertificateType === 4 && !this.distribution.sslCertificateId) {
        this.errorToast('Please select a certificate before creating the distribution.')
        return
      }
      this.distribution.originTypeId = this.origin.originTypeId
      this.distribution.bucket = !this.custom ? this.selectedBucket : null
      this.distribution.bucketAccessId = this.origin.bucketAccessId
      this.distribution.storageAccountId = !this.custom ? this.selectedAccount.id : null
      this.distribution.customHeaders = this.originHeaders
      this.addDisitrbution(this.distribution)
    },
    addDisitrbution(distribution) {
      this.$store
        .dispatch('cdn/addDistribution', distribution)
        .then(() => {
          this.successToast('Distribution was created')
          this.onCancel()
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while creating distribution.')
        })
    },
    showInfo(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
    validationOriginSettings() {
      return new Promise((resolve, reject) => {
        this.$refs.originRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    onAddField() {
      this.originHeaders.push({
        index: this.lastIndex + 1,
        key: '',
        value: '',
      })
    },
    onRemoveField(index) {
      this.originHeaders = this.originHeaders.filter(obj => obj.index !== index)

      for (let i = 0; i < this.originHeaders.length; i++) {
        this.originHeaders[i].index = i
      }
    },
    onCreateCachePolicies() {
      this.$store
        .dispatch('cdn/getBehaviorPolicies', {
          distributionId: 0,
          id: 0,
        })
        .then(() => {
          this.behaviorModel.cachePolicyOptions = this.$store.state.cdn.behaviorCachePolicyOptions
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while creating policy.')
        })
    },
    onCreateRequestPolicies() {
      this.$store
        .dispatch('cdn/getBehaviorPolicies', {
          distributionId: 0,
          id: 0,
        })
        .then(() => {
          this.behaviorModel.originRequestPolicyOptions = this.$store.state.cdn.behaviorOriginRequestPolicyOptions
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while creating policy.')
        })
    },
    onChangeOriginId() {
      if (!this.originIdChanged) {
        if (this.custom) {
          const id = this.distribution.domainName
          this.distribution.originId = id !== null ? `custom-${id.substring(0, 43)}` : ''
          this.$store.commit('accountBuckets/CANCEL_FETCHING_BUCKETS')
        } else {
          this.distribution.originId = this.originBucketId
        }
      }
    },
    onCancel() {
      this.$store.commit('cdn/SET_NEW_DISTRIBUTION')
      this.$router.push({ name: 'distributions' })
    },
    getBucketOptions: options => options.map(o => ({ text: o.name, value: o.name })),
    toggleOpenExternalAccount() {
      this.isOpenExternalAccount = !this.isOpenExternalAccount
    },
    changeSelectedAccount(account) {
      if (account === null) return null
      this.selectedAccount = account
      this.accountId = account?.id || 0
      const bucketName = this.selectedAccount?.bucketName

      // eslint-disable-next-line eqeqeq
      if (this.selectedAccount?.parentId == 0 || bucketName === null) {
        this.getBuckets()
      } else {
        this.accountBuckets = { name: bucketName }
        this.selectedBucket = bucketName
        this.$store.commit('accountBuckets/CANCEL_FETCHING_BUCKETS')
      }

      return this.selectedAccount
    },
    isBucketDisabled() {
      // eslint-disable-next-line eqeqeq
      return this.selectedAccount.bucketName !== null && this.selectedAccount.parentId != 0
    },
    formatCertificateLabel(cert) {
      const additional = cert.additionalNames ? ` (${cert.additionalNames})` : ''
      return `#${cert.id} - ${cert.domainName}${additional} - ${cert.certificateStatus}`
    },
    async fetchSniCertificates() {
      try {
        await this.$store.dispatch('cdn/fetchSniCertificates')
      } catch (error) {
        const message = error?.response?.data?.errors?.[0] ?? 'Failed to fetch sni certificates.'
        this.errorToast(message)
      }
    },
    beforeDestroy() {
      this.accountId = 0
    },
  },
}
</script>

<style lang="scss" scoped>
.custom-label-position {
  ::v-deep {
    .labelMargin {
      position: relative;
      left: -98px;
    }
  }
}

.hidden {
  opacity: 0;
}
.done-button {
  min-width: 57px;
}
.right-icon {
  position: absolute;
  left: 100%;
  margin-left: 10px;
}
.gj-wizard {
  ::v-deep {
    .wizard-tab-container {
      > div {
        margin: auto;
      }
    }
    .card.paper {
      .card-body {
        padding-bottom: 0;
      }
    }

    .info-assistant {
      margin-left: auto;
    }
  }
}

.mr-35 {
  margin-right: 35px;
}

.bucket-input::v-deep{
  .dropdown-menu {
    height: auto;
    max-height: 200px;
    overflow-y: auto;
  }
}

</style>
